// RETAIL PHASE 6 — frontend mirror of the pure UOM converter used for DISPLAY and
// live entry preview only. The BACKEND is authoritative: it re-converts every
// entered {qty, unit} to the base unit at the retail edge (createIndent / pick /
// receive), so this file can never corrupt a stored quantity. It exists so the
// retail screens can (a) show "10 kg (2 tin)" next to a base qty and (b) preview
// "2 tin = 10 kg" as the user types.
//
// Keep in lock-step with src/domain/packs/convert.ts (same rounding rule: snap to
// 3 dp, matching the numeric(16,3) base column, which also absorbs IEEE-754 error
// so round-trips through factors like 0.02 stay exact).

const QTY_SCALE = 1000; // 3 decimals

export function roundQty(x) {
  return Math.round(Number(x) * QTY_SCALE) / QTY_SCALE;
}

function isBaseUnit(unit, baseUomCode) {
  if (unit == null || String(unit).trim() === "") return true;
  if (baseUomCode && String(unit).trim().toLowerCase() === String(baseUomCode).trim().toLowerCase()) return true;
  return false;
}

function findPack(unit, packs) {
  const u = String(unit).trim().toLowerCase();
  return (packs || []).find((p) => String(p.packName).trim().toLowerCase() === u);
}

// qty in `unit` → base units. Unknown pack ⇒ NaN (caller guards); base ⇒ qty.
export function toBase(qty, unit, baseUomCode, packs) {
  const q = Number(qty);
  if (!Number.isFinite(q)) return NaN;
  if (isBaseUnit(unit, baseUomCode)) return roundQty(q);
  const pack = findPack(unit, packs);
  if (!pack || !(Number(pack.factorToBase) > 0)) return NaN;
  return roundQty(q * Number(pack.factorToBase));
}

// base qty → `unit`. Unknown pack ⇒ NaN; base ⇒ baseQty.
export function fromBase(baseQty, unit, baseUomCode, packs) {
  const b = Number(baseQty);
  if (!Number.isFinite(b)) return NaN;
  if (isBaseUnit(unit, baseUomCode)) return roundQty(b);
  const pack = findPack(unit, packs);
  if (!pack || !(Number(pack.factorToBase) > 0)) return NaN;
  return roundQty(b / Number(pack.factorToBase));
}

export function fmtNum(n) {
  return String(roundQty(n));
}

// "10 kg (2 tin)" — base qty in its base uom, plus a pack equivalent if a pack is
// given (and defined). No pack / unknown pack ⇒ just "10 kg".
export function formatBasePack(baseQty, baseUomCode, packName, packs) {
  const baseLabel = `${fmtNum(baseQty)}${baseUomCode ? " " + baseUomCode : ""}`;
  if (!packName || isBaseUnit(packName, baseUomCode)) return baseLabel;
  const pack = findPack(packName, packs);
  if (!pack) return baseLabel;
  const inPack = fromBase(baseQty, packName, baseUomCode, packs);
  if (!Number.isFinite(inPack)) return baseLabel;
  return `${baseLabel} (${fmtNum(inPack)} ${pack.packName})`;
}
